import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, CheckCircle2, ImageUp, Save, MapPin, Palette, Landmark, ReceiptText, FolderLock, ShieldCheck, Trash2 } from "lucide-react";
import AppShell from "../components/AppShell";
import BusinessDocumentsPanel from "../components/BusinessDocumentsPanel";
import FiscalSetupTab from "../components/FiscalSetupTab";
import { api, API_URL } from "../lib/api";

const TABS = [
  { id: "identidad", label: "Identidad", icon: Building2 },
  { id: "direccion", label: "Dirección", icon: MapPin },
  { id: "fiscal", label: "Configuración Fiscal", icon: ShieldCheck },
  { id: "facturacion", label: "Facturación", icon: ReceiptText },
  { id: "branding", label: "Branding", icon: Palette },
  { id: "banco", label: "Banco", icon: Landmark },
  { id: "documentos", label: "Documentos", icon: FolderLock },
];

const VALID_TABS = new Set(["identidad", "direccion", "fiscal", "facturacion", "branding", "banco", "documentos"]);

const REGIMES = [
  "RESICO", "Persona Física con Actividad Empresarial", "Régimen Simplificado",
  "Persona Moral", "Sueldos y Salarios", "Arrendamiento", "Actividades Agrícolas",
  "Ingresos por Intereses", "Sin obligaciones fiscales",
];
const CFDI_VERSIONS = ["4.0", "3.3"];
const PAYMENT_METHODS = [
  ["PUE", "PUE - Pago en una sola exhibición"],
  ["PPD", "PPD - Pago en parcialidades o diferido"],
];
const PAYMENT_FORMS = [
  ["01", "01 - Efectivo"], ["02", "02 - Cheque nominativo"], ["03", "03 - Transferencia electrónica"],
  ["04", "04 - Tarjeta de crédito"], ["28", "28 - Tarjeta de débito"], ["99", "99 - Por definir"],
];
const CURRENCIES = ["MXN", "USD", "EUR", "CAD"];

export default function BusinessProfilePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [tab, setTab] = useState(VALID_TABS.has(requestedTab) ? requestedTab : "identidad");
  useEffect(() => {
    // Keep URL in sync so deep links + refresh stay on the same tab.
    const current = searchParams.get("tab");
    if (tab === "identidad") {
      if (current) { const next = new URLSearchParams(searchParams); next.delete("tab"); setSearchParams(next, { replace: true }); }
    } else if (current !== tab) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", tab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [tab]);
  const [profile, setProfile] = useState({});
  const [validation, setValidation] = useState({ ready: true, missing: [] });
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const logoInput = useRef(null);

  const load = () => api.get("/business-profile", { withCredentials: true })
    .then((r) => setProfile(r.data))
    .catch(() => setError("No pudimos cargar tu perfil de negocio."));

  const loadValidation = () => api.get("/business-profile/validation", { withCredentials: true })
    .then((r) => setValidation(r.data))
    .catch(() => setValidation({ ready: true, missing: [] }));

  useEffect(() => { load(); loadValidation(); }, []);

  useEffect(() => {
    if (!profile?.logo_uploaded) { setLogoUrl(""); return; }
    const token = window.localStorage.getItem("session_token");
    fetch(`${API_URL}/business-profile/logo`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => blob && setLogoUrl(URL.createObjectURL(blob)))
      .catch(() => setLogoUrl(""));
  }, [profile?.logo_uploaded]);

  const setValue = (key, value) => setProfile((prev) => ({ ...prev, [key]: value }));

  const save = async (event) => {
    event?.preventDefault();
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const response = await api.put("/business-profile", profile, { withCredentials: true });
      setProfile(response.data);
      await loadValidation();
      setNotice("Listo. Estos datos se usarán en todas tus facturas y reportes.");
      setTimeout(() => setNotice(""), 3500);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos guardar los cambios.");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    const body = new FormData();
    body.append("file", file);
    try {
      await api.post("/business-profile/logo", body, { withCredentials: true });
      setNotice("Logo actualizado. Aparecerá en tus próximas facturas.");
      load();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos subir el logo.");
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("¿Eliminar el logo del perfil? Podrás subir otro después.")) return;
    setError("");
    try {
      await api.delete("/business-profile/logo", { withCredentials: true });
      setLogoUrl("");
      setNotice("Logo eliminado.");
      load();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos eliminar el logo.");
    }
  };

  const missingSet = new Set(validation.missing.map((m) => m.field));
  const requiredMark = (field) => missingSet.has(field) ? <span style={{ color: "#ef4444", marginLeft: 4 }}>*</span> : null;

  return (
    <AppShell>
      <div className="page">
        <section className="profile-hero">
          <div>
            <small className="eyebrow">PERFIL DE NEGOCIO</small>
            <h1>Tu empresa, <em>centralizada.</em></h1>
            <p>Configura una vez y Mi Contador usa estos datos en facturas, reportes, PDFs y emails.</p>
          </div>
          <div className="avatar-uploader" style={{ marginBottom: 0 }}>
            <span className="profile-menu-avatar large" style={{ background: profile?.brand_primary_color || undefined }}>
              {logoUrl ? <img src={logoUrl} alt="Logo" /> : <Building2 size={24} />}
            </span>
            <div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" onClick={() => logoInput.current?.click()} data-testid="business-logo-upload-button">
                  <ImageUp size={15} style={{ marginRight: 6 }} /> {uploading ? "Subiendo..." : (profile?.logo_uploaded ? "Cambiar logo" : "Subir logo")}
                </button>
                {profile?.logo_uploaded && (
                  <button type="button" onClick={removeLogo} data-testid="business-logo-delete-button" style={{ background: "#fef2f2", borderColor: "#fecaca", color: "#b91c1c" }}>
                    <Trash2 size={14} style={{ marginRight: 4 }} /> Eliminar
                  </button>
                )}
              </div>
              <input ref={logoInput} type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" style={{ display: "none" }} onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="business-logo-file-input" />
              <p style={{ margin: "8px 0 0", fontSize: 12, color: "#6b7280" }}>JPG, PNG, WEBP o SVG · Hasta 3 MB · Fondo transparente OK.</p>
            </div>
          </div>
        </section>

        {/* SAT validation banner */}
        <div
          className={`sat-validation-banner ${validation.ready ? "ready" : "warning"}`}
          data-testid="sat-validation-banner"
          style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "12px 16px", borderRadius: 14,
            background: validation.ready ? "#ecfdf5" : "#fef3c7",
            border: `1px solid ${validation.ready ? "#a7f3d0" : "#fde68a"}`,
            marginBottom: 16,
          }}
        >
          {validation.ready ? <CheckCircle2 size={20} color="#10b981" /> : <span style={{ color: "#f59e0b", fontSize: 20 }}>⚠️</span>}
          <div style={{ flex: 1 }}>
            <b style={{ font: '800 14px "Nunito", sans-serif', color: validation.ready ? "#065f46" : "#92400e" }}>
              {validation.ready
                ? "Perfil listo para emitir facturas oficiales"
                : `Faltan ${validation.missing.length} campo${validation.missing.length === 1 ? "" : "s"} obligatorio${validation.missing.length === 1 ? "" : "s"} del SAT`}
            </b>
            {!validation.ready && (
              <small style={{ display: "block", marginTop: 2, color: "#78350f", font: '600 12px "Nunito", sans-serif' }}>
                {validation.missing.map((m) => m.label).join(" · ")}
              </small>
            )}
          </div>
        </div>

        <nav className="business-tabs" data-testid="business-tabs">
          {TABS.map((t) => (
            <button key={t.id} type="button" className={tab === t.id ? "active" : ""} onClick={() => setTab(t.id)} data-testid={`business-tab-${t.id}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </nav>

        {notice && <p className="profile-notice" data-testid="business-profile-notice">{notice}</p>}
        {error && <p className="profile-error" data-testid="business-profile-error">{error}</p>}

        <form onSubmit={save}>
          {tab === "identidad" && (
            <section className="profile-section" data-testid="business-identity-section">
              <h2>Datos fiscales y de contacto</h2>
              <p>Estos datos aparecerán en el encabezado de tus facturas.</p>
              <div className="profile-grid">
                <label>Nombre comercial<input value={profile.business_name || ""} onChange={(e) => setValue("business_name", e.target.value)} data-testid="business-name-input" /></label>
                <label>Razón social{requiredMark("legal_name")}<input value={profile.legal_name || ""} onChange={(e) => setValue("legal_name", e.target.value)} data-testid="business-legal-name-input" /></label>
                <label>RFC{requiredMark("rfc")}<input value={profile.rfc || ""} onChange={(e) => setValue("rfc", e.target.value.toUpperCase())} data-testid="business-rfc-input" /></label>
                <label>Régimen fiscal{requiredMark("tax_regime")}<select value={profile.tax_regime || ""} onChange={(e) => setValue("tax_regime", e.target.value)} data-testid="business-tax-regime-input"><option value="">Selecciona</option>{REGIMES.map((r) => <option key={r}>{r}</option>)}</select></label>
                <label>CP fiscal (CFDI){requiredMark("cfdi_zip")}<input value={profile.cfdi_zip || ""} onChange={(e) => setValue("cfdi_zip", e.target.value)} data-testid="business-cfdi-zip-input" /></label>
                <label>Actividad económica<input value={profile.business_activity || ""} onChange={(e) => setValue("business_activity", e.target.value)} data-testid="business-activity-input" /></label>
                <label className="full">Obligaciones fiscales (opcional)<textarea placeholder="Ej: ISR Personas Morales, IVA, Retenciones..." value={profile.tax_obligations || ""} onChange={(e) => setValue("tax_obligations", e.target.value)} data-testid="business-tax-obligations-input" /></label>
                <label>Correo de negocio<input type="email" value={profile.email || ""} onChange={(e) => setValue("email", e.target.value)} data-testid="business-email-input" /></label>
                <label>Teléfono<input value={profile.phone || ""} onChange={(e) => setValue("phone", e.target.value)} data-testid="business-phone-input" /></label>
                <label>Sitio web<input placeholder="https://..." value={profile.website || ""} onChange={(e) => setValue("website", e.target.value)} data-testid="business-website-input" /></label>
                <label>Horario<input placeholder="Lun a Vie · 9:00 a 18:00" value={profile.business_hours || ""} onChange={(e) => setValue("business_hours", e.target.value)} data-testid="business-hours-input" /></label>
                <label className="full">Descripción del negocio<textarea value={profile.business_description || ""} onChange={(e) => setValue("business_description", e.target.value)} data-testid="business-description-input" /></label>
              </div>
            </section>
          )}

          {tab === "direccion" && (
            <section className="profile-section" data-testid="business-address-section">
              <h2>Dirección fiscal</h2>
              <p>Se usará en tus facturas y como lugar de expedición cuando aplique.</p>
              <div className="profile-grid">
                <label className="full">Calle<input value={profile.street || ""} onChange={(e) => setValue("street", e.target.value)} data-testid="business-street-input" /></label>
                <label>Número exterior<input value={profile.exterior_number || ""} onChange={(e) => setValue("exterior_number", e.target.value)} data-testid="business-ext-input" /></label>
                <label>Número interior<input value={profile.interior_number || ""} onChange={(e) => setValue("interior_number", e.target.value)} data-testid="business-int-input" /></label>
                <label>Colonia<input value={profile.neighborhood || ""} onChange={(e) => setValue("neighborhood", e.target.value)} data-testid="business-neighborhood-input" /></label>
                <label>Código postal<input value={profile.postal_code || ""} onChange={(e) => setValue("postal_code", e.target.value)} data-testid="business-postal-input" /></label>
                <label>Ciudad<input value={profile.city || ""} onChange={(e) => setValue("city", e.target.value)} data-testid="business-city-input" /></label>
                <label>Estado<input value={profile.state || ""} onChange={(e) => setValue("state", e.target.value)} data-testid="business-state-input" /></label>
                <label>País<input value={profile.country || "México"} onChange={(e) => setValue("country", e.target.value)} data-testid="business-country-input" /></label>
                <label className="full">Dirección completa (una línea)<input placeholder="Se usa como respaldo en facturas antiguas" value={profile.address || ""} onChange={(e) => setValue("address", e.target.value)} data-testid="business-address-input" /></label>
                <label className="full">Google Maps (URL)<input placeholder="https://maps.google.com/..." value={profile.maps_url || ""} onChange={(e) => setValue("maps_url", e.target.value)} data-testid="business-maps-input" /></label>
              </div>
            </section>
          )}

          {tab === "facturacion" && (
            <section className="profile-section" data-testid="business-invoice-section">
              <h2>Valores por defecto para facturas</h2>
              <p>Cada vez que crees una factura, Mi Contador usará estos valores automáticamente.</p>
              <div className="profile-grid">
                <label>Serie<input placeholder="A" value={profile.invoice_series || ""} onChange={(e) => setValue("invoice_series", e.target.value)} data-testid="business-series-input" /></label>
                <label>Folio inicial<input type="number" min="1" value={profile.invoice_starting_folio || ""} onChange={(e) => setValue("invoice_starting_folio", e.target.value)} data-testid="business-folio-input" /></label>
                <label>Moneda por defecto<select value={profile.default_currency || "MXN"} onChange={(e) => setValue("default_currency", e.target.value)} data-testid="business-currency-input">{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select></label>
                <label>IVA por defecto (%)<input type="number" min="0" max="30" step="0.5" value={profile.default_iva_rate ?? 16} onChange={(e) => setValue("default_iva_rate", Number(e.target.value))} data-testid="business-iva-input" /></label>
                <label>Método de pago<select value={profile.default_payment_method || ""} onChange={(e) => setValue("default_payment_method", e.target.value)} data-testid="business-payment-method-input"><option value="">Sin definir</option>{PAYMENT_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
                <label>Forma de pago<select value={profile.default_payment_form || ""} onChange={(e) => setValue("default_payment_form", e.target.value)} data-testid="business-payment-form-input"><option value="">Sin definir</option>{PAYMENT_FORMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
                <label>Condiciones de pago<input placeholder="Contado, 30 días..." value={profile.default_payment_terms || ""} onChange={(e) => setValue("default_payment_terms", e.target.value)} data-testid="business-payment-terms-input" /></label>
                <label>Versión CFDI<select value={profile.cfdi_version || "4.0"} onChange={(e) => setValue("cfdi_version", e.target.value)} data-testid="business-cfdi-version-input">{CFDI_VERSIONS.map((v) => <option key={v}>{v}</option>)}</select></label>
                <label>Código de exportación<input placeholder="01 - No aplica" value={profile.export_code || ""} onChange={(e) => setValue("export_code", e.target.value)} data-testid="business-export-code-input" /></label>
                <label>Lugar de expedición<input placeholder="CP de expedición (default: CP fiscal)" value={profile.place_of_issuance || ""} onChange={(e) => setValue("place_of_issuance", e.target.value)} data-testid="business-place-input" /></label>
                <label className="full">Notas de factura (opcional)<textarea placeholder="Instrucciones o cláusulas que aparecerán en cada factura" value={profile.invoice_notes || ""} onChange={(e) => setValue("invoice_notes", e.target.value)} data-testid="business-invoice-notes-input" /></label>
              </div>
            </section>
          )}

          {tab === "branding" && (
            <section className="profile-section" data-testid="business-branding-section">
              <h2>Branding y comunicación</h2>
              <p>Colores, textos y redes que aparecen en facturas, PDFs y correos.</p>
              <div className="profile-grid">
                <label>Color primario<input type="color" value={profile.brand_primary_color || "#4169e1"} onChange={(e) => setValue("brand_primary_color", e.target.value)} data-testid="business-primary-color" /></label>
                <label>Color de acento<input type="color" value={profile.brand_accent_color || "#28a878"} onChange={(e) => setValue("brand_accent_color", e.target.value)} data-testid="business-accent-color" /></label>
                <label>Slogan<input value={profile.company_slogan || ""} onChange={(e) => setValue("company_slogan", e.target.value)} data-testid="business-slogan-input" /></label>
                <label>Sitio web<input placeholder="https://..." value={profile.website || ""} onChange={(e) => setValue("website", e.target.value)} data-testid="business-website-branding-input" /></label>
                <label className="full">Pie de factura<textarea placeholder="Gracias por tu preferencia..." value={profile.invoice_footer || ""} onChange={(e) => setValue("invoice_footer", e.target.value)} data-testid="business-footer-input" /></label>
                <label className="full">Firma de correo<textarea value={profile.email_signature || ""} onChange={(e) => setValue("email_signature", e.target.value)} data-testid="business-email-signature-input" /></label>
                <label>Facebook<input placeholder="https://facebook.com/..." value={profile.facebook_url || ""} onChange={(e) => setValue("facebook_url", e.target.value)} data-testid="business-facebook-input" /></label>
                <label>Instagram<input placeholder="https://instagram.com/..." value={profile.instagram_url || ""} onChange={(e) => setValue("instagram_url", e.target.value)} data-testid="business-instagram-input" /></label>
                <label>LinkedIn<input placeholder="https://linkedin.com/..." value={profile.linkedin_url || ""} onChange={(e) => setValue("linkedin_url", e.target.value)} data-testid="business-linkedin-input" /></label>
                <label>X (Twitter)<input placeholder="https://x.com/..." value={profile.twitter_url || ""} onChange={(e) => setValue("twitter_url", e.target.value)} data-testid="business-twitter-input" /></label>
              </div>
            </section>
          )}

          {tab === "banco" && (
            <section className="profile-section" data-testid="business-bank-section">
              <h2>Datos bancarios</h2>
              <p>Aparecerán en facturas cuando la forma de pago sea transferencia.</p>
              <div className="profile-grid">
                <label>Banco<input value={profile.bank_name || ""} onChange={(e) => setValue("bank_name", e.target.value)} data-testid="business-bank-name-input" /></label>
                <label>Titular<input value={profile.bank_account_holder || ""} onChange={(e) => setValue("bank_account_holder", e.target.value)} data-testid="business-bank-holder-input" /></label>
                <label>Número de cuenta<input value={profile.bank_account_number || ""} onChange={(e) => setValue("bank_account_number", e.target.value)} data-testid="business-bank-account-input" /></label>
                <label>CLABE<input value={profile.bank_clabe || ""} onChange={(e) => setValue("bank_clabe", e.target.value)} data-testid="business-bank-clabe-input" /></label>
                <label>SWIFT / BIC<input value={profile.bank_swift || ""} onChange={(e) => setValue("bank_swift", e.target.value)} data-testid="business-bank-swift-input" /></label>
                <label>Moneda<select value={profile.bank_currency || "MXN"} onChange={(e) => setValue("bank_currency", e.target.value)} data-testid="business-bank-currency-input">{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select></label>
              </div>
            </section>
          )}

          {tab === "documentos" && <BusinessDocumentsPanel />}

          {tab === "fiscal" && <FiscalSetupTab />}

          {tab !== "documentos" && tab !== "fiscal" && (
            <button className="profile-save" type="submit" disabled={saving} data-testid="business-profile-save-button">
              <Save size={16} /> {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          )}
        </form>
      </div>
    </AppShell>
  );
}
